import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";

/**
 * AnimatedMiniPlayer
 * ---------------------------------------------------------------
 * A compact animation preview component.
 *
 * Props:
 *  - type: "FADE" | "SLIDE" | "FLIP" | "ZOOM" | "SCROLL"
 *  - direction: depends on type
 *      FLIP: "HORIZONTAL" | "VERTICAL"
 *      SLIDE: "LEFT" | "RIGHT" | "UP" | "DOWN"
 *      ZOOM: "CENTER" | "LEFT" | "RIGHT" | "UP" | "DOWN"
 *      SCROLL: "LEFT" | "RIGHT" | "UP" | "DOWN"
 *  - intervalMs?: number  (time before switching items, default 1400)
 *  - durationMs?: number  (animation duration, default 600)
 *  - className?: string   (optional container overrides)
 *
 * Behavior:
 *  - Shows a 340x220 outer player. Inside it, a stage (320x200) that
 *    cycles three colored rectangles with the selected animation.
 *  - SCROLL mode continuously scrolls the three rectangles.
 */

const COLORS = ["#49A6E9", "#B7D9FC", "#96F1F8"];

const VALID = {
  FADE: ["CENTER"],
  DISSOLVE: ["CENTER"],
  SLIDE: ["LEFT", "RIGHT", "UP", "DOWN"],
  FLIP: ["HORIZONTAL", "VERTICAL"],
  ZOOM: ["CENTER", "LEFT", "RIGHT", "UP", "DOWN"],
  SCROLL: ["LEFT", "RIGHT", "UP", "DOWN"],
  ROLL: ["LEFT", "RIGHT", "UP", "DOWN"],
};

function clampDirection(type, direction) {
  const list = VALID[type] || [];
  if (!list.length) return "CENTER";
  return list.includes(direction) ? direction : list[0];
}

export default function AnimatedMiniPlayer({
  enabled = true,
  type = "FADE",
  direction = "CENTER",
  intervalMs = 1400,
  durationMs = 200,
  className = "",
}) {
  const { t } = useTranslation();


  const safeType = (Object.keys(VALID).includes(type) ? type : "FADE");
  const safeDirection = clampDirection(safeType, direction);
  const [index, setIndex] = useState(0);
  const next = () => setIndex((i) => (i + 1) % 3);

  // Switch items on an interval, except in SCROLL (handled separately)
  useEffect(() => {
    if (safeType === "SCROLL") return;
    const id = setInterval(next, Math.max(600, intervalMs));
    return () => clearInterval(id);
  }, [safeType, intervalMs]);

  // Variants factory by type/direction
  const variants = useMemo(() => {
    const dur = durationMs / 1000;
    const common = { duration: dur, ease: [0.22, 1, 0.36, 1] };

    if (safeType === "FADE") {
      return {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: common },
        exit: { opacity: 0, transition: common },
      };
    }

    if (safeType === "SLIDE") {
      const distX = 60; // px
      const distY = 40;
      const map = {
        LEFT: { x: -distX, y: 0 },
        RIGHT: { x: distX, y: 0 },
        UP: { x: 0, y: -distY },
        DOWN: { x: 0, y: distY },
      };
      const from = map[safeDirection];
      return {
        initial: { opacity: 0, ...from },
        animate: { opacity: 1, x: 0, y: 0, transition: common },
        exit: { opacity: 0, ...from, transition: common },
      };
    }

    if (safeType === "FLIP") {
      const axis = safeDirection === "VERTICAL" ? "rotateX" : "rotateY";
      return {
        initial: { opacity: 0.2, [axis]: -90 },
        animate: { opacity: 1, [axis]: 0, transition: common },
        exit: { opacity: 0.2, [axis]: 90, transition: common },
      };
    }

    if (safeType === "ZOOM") {
      const originMap = {
        CENTER: "50% 50%",
        LEFT: "0% 50%",
        RIGHT: "100% 50%",
        UP: "50% 0%",
        DOWN: "50% 100%",
      };
      const origin = originMap[safeDirection] || originMap.CENTER;

      return {
        initial: { opacity: 0, scale: 0.85, transformOrigin: origin },
        animate: { opacity: 1, scale: 1, transformOrigin: origin, transition: common },
        exit: { opacity: 0, scale: 0.6, transformOrigin: origin, transition: common },
      };
    }


    if (safeType === "DISSOLVE") {
      return {
        initial: { opacity: 0 },
        animate: {
          opacity: 1,
          transition: { duration: dur, ease: "easeInOut" },
        },
        exit: {
          opacity: 0,
          transition: { duration: dur, ease: "easeInOut" },
        },
      };
    }

    if (safeType === "ROLL") {
      // distance it travels in from the edge + spin degrees
      const distX = 140;  // bigger = rolls from farther left/right
      const distY = 100;  // bigger = rolls from farther top/bottom
      const spin = 120;   // degrees; tweak 180–360 for taste

      // per-direction offsets and spin sign (so motion feels natural)
      const DIR = {
        LEFT: {
          from: { x: -distX, y: 0 },
          to: { x: 0, y: 0 },
          exit: { x: distX, y: 0 }, // opposite side
          spinIn: spin,
          spinOut: spin,
        },
        RIGHT: {
          from: { x: distX, y: 0 },
          to: { x: 0, y: 0 },
          exit: { x: -distX, y: 0 },
          spinIn: -spin,
          spinOut: -spin,
        },
        UP: {
          from: { x: 0, y: -distY },
          to: { x: 0, y: 0 },
          exit: { x: 0, y: distY },
          spinIn: -spin,
          spinOut: -spin,
        },
        DOWN: {
          from: { x: 0, y: distY },
          to: { x: 0, y: 0 },
          exit: { x: 0, y: -distY },
          spinIn: spin,
          spinOut: spin,
        },
      }[safeDirection] || {
        from: { x: -distX, y: 0 },
        to: { x: 0, y: 0 },
        exit: { x: distX, y: 0 },
        spinIn: spin,
        spinOut: spin,
      };

      // slightly different timing to feel “settled”
      const tIn = { duration: common.duration * 0.5, ease: [0.22, 1, 0.36, 1] };  // smooth decel
      const tOut = { duration: common.duration * 0.85, ease: [0.4, 0, 0.6, 1] };  // snappier out

      return {
        // start off-screen with rotation
        initial: {
          opacity: 0.1,
          x: DIR.from.x,
          y: DIR.from.y,
          rotateZ: DIR.spinIn,               // a chunk of a turn
          transformOrigin: "50% 50%",
        },

        // roll in and “settle” at center
        animate: {
          opacity: 1,
          x: 0,
          y: 0,
          rotateZ: 0,                        // ends perfectly upright
          transition: tIn,
        },

        // roll away in the same direction it came from
        exit: {
          opacity: 0.01,
          x: DIR.exit.x,
          y: DIR.exit.y,
          rotateZ: DIR.spinOut,              // opposite spin out
          transition: tOut,
        },
      };
    }

    // SCROLL handled separately
    return {};
  }, [safeType, safeDirection, durationMs]);

  // SCROLL controls
  const trackControls = useAnimationControls();
  const isHorizontal = safeDirection === "LEFT" || safeDirection === "RIGHT";
  const CARD_W = 320;
  const CARD_H = 200;
  const GAP = 0;
  const firstStripRef = useRef(null);
  useEffect(() => {
    if (safeType !== "SCROLL") return;

    // 1) Stop any running tween and hard-reset both axes
    trackControls.stop();
    trackControls.set({ x: 0, y: 0 });

    // ---- 1) Measure the first strip length exactly ----
    const stripEl = firstStripRef.current;
    let stripLength = 0;
    if (stripEl) {
      const rect = stripEl.getBoundingClientRect();
      stripLength = isHorizontal ? rect.width : rect.height;

      // Defensive: include computed gap between last of first strip and first of duplicate
      const styles = window.getComputedStyle(stripEl.parentElement); // the flex container
      const gapPx = parseFloat(styles.gap || "0");
      stripLength += gapPx; // creates perfect seam across strips
    }

    // Fallback if measurement fails
    if (!stripLength) {
      // Worst case: assume each item width/height + gap times N
      const N = COLORS.length;
      const cardW = 320, cardH = 200; // your Card size
      const unit = (isHorizontal ? cardW : cardH) + GAP;
      stripLength = N * unit + GAP; // include seam gap to duplicate
    }

    const oneCycleMs = Math.max(1200, intervalMs * 1.2);
    const forward = safeDirection === "RIGHT" || safeDirection === "DOWN";

    // We always move negative (left/up) for the marquee logic;
    // for RIGHT/DOWN we flip the flex order so the visual motion is correct.
    const from = 0;
    const to = -stripLength;

    // start
    if (isHorizontal) {
      trackControls.start({
        x: [from, to],
        y: 0,
        transition: {
          repeat: Infinity,
          ease: "linear",
          duration: oneCycleMs / 300,
        },
      });
    } else {
      trackControls.start({
        y: [from, to],
        x: 0,
        transition: {
          repeat: Infinity,
          ease: "linear",
          duration: oneCycleMs / 300,
        },
      });
    }

    return () => trackControls.stop();
  }, [safeType, safeDirection, isHorizontal, intervalMs, COLORS.length]);


  // Card element reused
  const Card = ({ color, i }) => (
    <div
      className="w-[320px] h-[200px]  shadow-md border border-black/10 flex items-center justify-center text-white font-medium"
      style={{ background: color }}
    >
      <span className="text-lg tracking-wide">{t('show.show_item_number', { number: i + 1 })}</span>
    </div>
  );


  if (!enabled) {
    const staticVariants = {
      initial: { opacity: 1, x: 0, y: 0, rotateZ: 0, scale: 1 },
      animate: { opacity: 1, x: 0, y: 0, rotateZ: 0, scale: 1, transition: { duration: 0 } },
      exit: { opacity: 1, x: 0, y: 0, rotateZ: 0, scale: 1, transition: { duration: 0 } },
    };
    return (
      <div className="  rounded-2xl bg-gray-300   
         backdrop-blur-sm p-2  border-[10px]
         shadow-[0_8px_24px_rgba(0,0,0,0.1)]  relative w-[320px] h-[200px] overflow-hidden" >

        <AnimatePresence mode="sync">
          {[index].map((i) => (
            <motion.div
              key={i}
              className="absolute inset-0"
              initial={enabled ? variants.initial : staticVariants.initial}
              animate={enabled ? variants.animate : staticVariants.animate}
              exit={enabled ? variants.exit : staticVariants.exit}
            >
              <Card color={COLORS[i]} i={i} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div >

    );
  }

  return (
    <div
      className={
        "relative w-[340px] h-[220px] rounded-2xl bg-gray-300   " +
        "backdrop-blur-sm p-2 overflow-hidden " +
        "shadow-[0_8px_24px_rgba(0,0,0,0.1)] " +
        className
      }
    >
      <div className="w-full h-full rounded-xl bg-neutral-800/50 flex items-center justify-center overflow-hidden">
        {/* SCROLL mode */}
        {safeType === "SCROLL" ? (
          <div className="relative w-full h-full overflow-hidden">
            <motion.div
              key={isHorizontal ? `axis-h-${safeDirection}` : `axis-v-${safeDirection}`} // remount on axis/dir changes
              className={
                "absolute flex gap-0 will-change-transform " +
                (isHorizontal
                  ? (safeDirection === "RIGHT" ? "flex-row-reverse " : "flex-row ") + "top-0 -translate-y-1/2"
                  : (safeDirection === "DOWN" ? "flex-col-reverse " : "flex-col ") + "left-0 -translate-x-1/2")
              }
              animate={trackControls}
              initial={{ x: 0, y: 0 }}
              transition={{ duration: 0 }}
              style={
                isHorizontal
                  ? { left: 0, right: undefined }
                  : { top: 0, bottom: undefined }
              }
            >
              {/* First strip (measured) */}
              <div ref={firstStripRef} className={isHorizontal ? "flex gap-0" : "flex flex-col gap-0"}>
                {COLORS.map((c, i) => <Card key={i} color={c} i={i} />)}
              </div>

              {/* Duplicate strip for infinite loop */}
              <div className={isHorizontal ? "flex gap-0" : "flex flex-col gap-0"}>
                {COLORS.map((c, i) => <Card key={`dup-${i}`} color={c} i={i} />)}
              </div>
            </motion.div>
          </div>

        ) : (
          safeType === "DISSOLVE" ?
            <div className="relative w-[320px] h-[200px]">

              <AnimatePresence mode="sync">
                {[index].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute inset-0"
                    initial={variants.initial}
                    animate={variants.animate}
                    exit={variants.exit}
                  >
                    <Card color={COLORS[i]} i={i} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            :
            <>
              {/* // SWITCHING modes (FADE/SLIDE/FLIP/ZOOM) */}
              <div className="relative w-[320px] h-[200px]">
                <AnimatePresence mode="wait">{
                  [index].map((i) => (
                    <motion.div
                      key={i}
                      className="absolute inset-0"
                      initial={variants.initial}
                      animate={variants.animate}
                      exit={variants.exit}
                    >
                      <Card color={COLORS[i]} i={i} />
                    </motion.div>
                  ))
                }</AnimatePresence>
              </div>
            </>
        )}
      </div>

      {/* HUD */}
      <div className="absolute bottom-2 right-3 text-[11px] text-neutral-300/80 select-none">
        <span className="px-2 py-1 rounded-md bg-black/30 border border-white/10">
          {safeType}
          {safeType !== "FADE" && <>
            <span className="mx-1">•</span>
            {safeDirection}
          </>}
        </span>
      </div>

      {/* Invisible click surface to advance in switching modes */}
      {safeType !== "SCROLL" && (
        <button
          type="button"
          // onClick={next}
          className="absolute inset-0 focus:outline-none"
          aria-label={t('show.show_next_item')}
          title={t('show.show_click_to_switch')}
        />
      )}
    </div>
  );
}

